import React, { useState } from 'react';
import { addClient, formatTestSiteName } from '../utils/storage';
import { ModalTemplate } from './templates';
import './Modal.css';

export default function CreateClientModal({ onClose, onCreated }) {
  const [clientName, setClientName] = useState('');
  const [city, setCity] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Custom test site name: null means auto-generated, string means user override
  const [customName, setCustomName] = useState(null);
  const [editingPreview, setEditingPreview] = useState(false);

  const autoName = clientName.trim() && city.trim()
    ? formatTestSiteName(clientName, city)
    : '';
  const displayName = customName !== null ? customName : autoName;

  function handleSubmit(e) {
    e.preventDefault();
    if (!clientName.trim() || !city.trim()) return;
    setCustomName(null);
    setConfirming(true);
  }

  async function handleConfirm() {
    setSaving(true);
    try {
      const override = customName !== null && customName !== autoName ? customName : null;
      await addClient(clientName.trim(), city.trim(), override);
      onCreated();
    } catch (err) {
      console.error('Failed to create client:', err);
      setSaveError('Failed to create test site. Please check your connection and try again.');
      setSaving(false);
    }
  }

  function handlePreviewDoubleClick() {
    setEditingPreview(true);
    if (customName === null) {
      setCustomName(autoName);
    }
  }

  function handlePreviewBlur() {
    setEditingPreview(false);
    if (!customName || customName.trim() === '' || customName.trim() === autoName) {
      setCustomName(null);
    }
  }

  function handlePreviewKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      setEditingPreview(false);
      if (!customName || customName.trim() === '' || customName.trim() === autoName) {
        setCustomName(null);
      }
    }
    if (e.key === 'Escape') {
      setEditingPreview(false);
      setCustomName(null);
    }
  }

  return (
    <ModalTemplate title="New Test Site" onClose={onClose} isDirty={isDirty}>
      {!confirming ? (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="clientName">Client Name</label>
            <input
              id="clientName"
              type="text"
              value={clientName}
              onChange={(e) => { setClientName(e.target.value); setIsDirty(true); setSaveError(null); }}
              autoFocus
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="city">City</label>
            <input
              id="city"
              type="text"
              value={city}
              onChange={(e) => { setCity(e.target.value); setIsDirty(true); setSaveError(null); }}
              required
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Next
            </button>
          </div>
        </form>
      ) : (
        <div>
          <div className="form-group">
            <label>Suggested Test Site</label>
            {editingPreview ? (
              <input
                className="confirm-name-input"
                type="text"
                value={customName !== null ? customName : autoName}
                onChange={(e) => setCustomName(e.target.value)}
                onBlur={handlePreviewBlur}
                onKeyDown={handlePreviewKeyDown}
                autoFocus
              />
            ) : (
              <div className="confirm-name-locked" onDoubleClick={handlePreviewDoubleClick}>
                <span className="confirm-name-value">{displayName}</span>
                {customName !== null ? (
                  <button
                    type="button"
                    className="preview-revert-btn"
                    onClick={(e) => { e.stopPropagation(); setCustomName(null); }}
                    title="Revert to suggested name"
                  >
                    ↺ Revert
                  </button>
                ) : (
                  <span className="confirm-name-lock">🔒</span>
                )}
              </div>
            )}
            <span className="confirm-hint">Double-click to edit the suggested test site</span>
          </div>
          {saveError && <div className="alert alert-danger" style={{ marginTop: 12 }}>{saveError}</div>}
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setConfirming(false); setCustomName(null); setEditingPreview(false); }}
              disabled={saving}
            >
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirm}
              disabled={saving}
            >
              {saving ? 'Creating...' : 'Create Test Site'}
            </button>
          </div>
        </div>
      )}
    </ModalTemplate>
  );
}
